import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

export const MusicSelector = ({ 
  userId,
  onMusicChange,
  disabled = false 
}) => {
  const [selectedMusic, setSelectedMusic] = useState('none');
  const [musicVolume, setMusicVolume] = useState(0.15);
  const [userPreference, setUserPreference] = useState(null);

  const musicTracks = [
    { id: 'none', name: 'None', url: null },
    { id: 'epic', name: 'Epic', url: '/audio/music/epic.mp3' },
    { id: 'energetic-rock', name: 'Energetic Rock', url: '/audio/music/energetic-rock.mp3' },
    { id: 'classical', name: 'Classical Beethoven', url: '/audio/music/classical.mp3' },
    { id: 'meditation', name: 'Meditation', url: '/audio/music/meditation.mp3' },
    { id: 'forest', name: 'Forest Meditation', url: '/audio/music/forest.mp3' }
  ];

  useEffect(() => {
    loadUserPreference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const loadUserPreference = async () => {
    if (!userId) return;

    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('audio_preferences')
        .eq('id', userId)
        .single();

      if (data && data.audio_preferences) {
        const prefs = data.audio_preferences;
        if (prefs.preferred_music) {
          setSelectedMusic(prefs.preferred_music);
          
          // Notify parent of saved preference
          const selectedTrack = musicTracks.find(t => t.id === prefs.preferred_music);
          const volume = prefs.music_volume !== undefined ? prefs.music_volume : 0.15;
          
          onMusicChange({
            id: prefs.preferred_music,
            url: selectedTrack?.url,
            volume: volume
          });
        }
        if (prefs.music_volume !== undefined) {
          setMusicVolume(prefs.music_volume);
        }
      }
    } catch (err) {
      console.error('Error loading music preference:', err);
    }
  };

  const handleMusicChange = async (e) => {
    const musicId = e.target.value;
    setSelectedMusic(musicId);

    const selectedTrack = musicTracks.find(t => t.id === musicId);
    
    onMusicChange({
      id: musicId,
      url: selectedTrack?.url,
      volume: musicVolume
    });

    await savePreference(musicId, musicVolume);
  };

  const handleVolumeChange = async (e) => {
    const volume = parseFloat(e.target.value);
    setMusicVolume(volume);

    const selectedTrack = musicTracks.find(t => t.id === selectedMusic);
    
    onMusicChange({
      id: selectedMusic,
      url: selectedTrack?.url,
      volume: volume
    });

    await savePreference(selectedMusic, volume);
  };

  const savePreference = async (musicId, volume) => {
    if (!userId) return;

    try {
      const newPrefs = {
        ...(userPreference || {}),
        preferred_music: musicId,
        music_volume: volume
      };

      const { error } = await supabase
        .from('user_profiles')
        .update({ audio_preferences: newPrefs })
        .eq('id', userId);

      if (error) throw error;
      setUserPreference(newPrefs);
    } catch (err) {
      console.error('Error saving music preference:', err);
    }
  };

  return (
    <div style={styles.container}>
      <label style={styles.label}>Background Music</label>
      
      <select
        value={selectedMusic}
        onChange={handleMusicChange}
        disabled={disabled}
        style={styles.select}
      >
        {musicTracks.map(track => (
          <option key={track.id} value={track.id}>
            {track.name}
          </option>
        ))}
      </select>
      
      {selectedMusic !== 'none' && (
        <p style={styles.hint}>
          🎵 Background music will play during your reading
        </p>
      )}
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#cbd5e1',
  },
  select: {
    width: '100%',
    padding: '0.8571em',
    border: '1px solid #2a3142',
    borderRadius: '8px',
    fontSize: '0.875rem',
    backgroundColor: '#0f1419',
    color: '#e2e8f0',
    cursor: 'pointer',
  },
  volumeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px',
  },
  volumeIcon: {
    fontSize: '1.125rem',
    color: '#94a3b8',
  },
  volumeSlider: {
    flex: 1,
    height: '6px',
    background: '#2a3142',
    borderRadius: '3px',
    outline: 'none',
    cursor: 'pointer',
  },
  volumeValue: {
    fontSize: '0.75rem',
    color: '#cbd5e1',
    minWidth: '40px',
    textAlign: 'right',
  },
  hint: {
    fontSize: '0.75rem',
    color: '#94a3b8',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
};